import { useOrganizationStorage } from '../../../../../../AppClients';
import { OrganizationWithExceededLimitConfirmDialog } from '../../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationWithExceededLimitConfirmDialog';
import { getOrgWithExceededLimit } from '../../../../../../ui/features/organizations/components/OrganizationsPanel/utils';
import { useContactManagementTranslation } from '../../../../../../ui/hooks';
import { DataTree, useDataTreeUsingState } from '@juvare/common-data-table-ui/DataTree';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, FormLabel, useMediaQuery } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';
import { useOrganizationSelectionTree } from '../../../hooks/useOrganizationSelectionTree';
import { showOrganizationTreeDialog } from '../../OrganizationsTreeDialog/OrganizationsTreeDialog';
// FIXME after CUI-532 is closed.
export const OrganizationsSelection = (props) => {
    const [treeRenderKey, setTreeRenderKey] = React.useState(v4());
    const handleTreeRenderKeyChange = () => setTreeRenderKey(v4());
    return (React.createElement(OrganizationsSelectionChild, Object.assign({ key: treeRenderKey }, props, { onChangeOrganizationIds: (newOrganizationIds) => {
            props.onChangeOrganizationIds(newOrganizationIds);
            handleTreeRenderKeyChange();
        } })));
};
export const OrganizationsSelectionChild = (props) => {
    const { t } = useContactManagementTranslation();
    const settings = useSelector(getSettings);
    const organizationStorage = useOrganizationStorage();
    const downSmScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { dataTreeProps, activeOrganizationId } = useOrganizationSelectionTree({
        useDataTreeFunction: useDataTreeUsingState,
        filteredOrganizationsIds: props.organizationIds,
    });
    return (React.createElement("div", { "data-test": 'select-organizations-table' },
        React.createElement(Box, { sx: [{ mb: 2 }, downSmScreen ? { mt: 1 } : {}] },
            React.createElement(Button, { startIcon: React.createElement(AddIcon, null), "data-test": 'add-organizations-button', color: 'primary', variant: 'outlined', onClick: () => {
                    showOrganizationTreeDialog({
                        showSelectAllChildren: props.showSelectAllChildren,
                        initialSelectedOrganizationIds: props.organizationIds,
                        multiSelect: true,
                        staticFilters: props.staticDialogFilters,
                        onChange: (addedOrganizationIds, orgTree) => {
                            const uniqueOrganizationIds = Array.from(new Set([...props.organizationIds, ...addedOrganizationIds]));
                            if (!settings.accountMembershipLimitPerOrgSubTreeEnabled || !orgTree) {
                                props.onChangeOrganizationIds(uniqueOrganizationIds);
                                return;
                            }
                            organizationStorage
                                .list({ filter: [{ id: uniqueOrganizationIds, op: 'in' }] })
                                .then((data) => {
                                const orgWithExceededLimit = getOrgWithExceededLimit(orgTree, data, props.organizationIds);
                                const orgWithExceededLimitArr = orgWithExceededLimit
                                    ? Array.from(orgWithExceededLimit.entries()).map(([id, name]) => ({ id, name }))
                                    : undefined;
                                if (!(orgWithExceededLimitArr === null || orgWithExceededLimitArr === void 0 ? void 0 : orgWithExceededLimitArr.length)) {
                                    props.onChangeOrganizationIds(uniqueOrganizationIds);
                                    return;
                                }
                                showConfirmDialog({
                                    title: t('publicSelfRegistration.membership.accountCantBeAdded'),
                                    content: (React.createElement(OrganizationWithExceededLimitConfirmDialog, { orgWithExceededLimit: orgWithExceededLimitArr })),
                                    confirmLabel: t('common.continue'),
                                    onConfirm: () => {
                                        props.onChangeOrganizationIds(uniqueOrganizationIds.filter((uniqueId) => !(orgWithExceededLimit === null || orgWithExceededLimit === void 0 ? void 0 : orgWithExceededLimit.has(uniqueId))));
                                    },
                                });
                            })
                                .catch(handleErrorDialog);
                        },
                    });
                } }, props.addLabel || t('panels.organizations.addOrgs')),
            activeOrganizationId && (React.createElement(Button, { "data-test": 'remove-organizations-button', sx: [{ ml: 2 }, downSmScreen ? { display: 'block', mt: 1, ml: 0 } : {}], color: 'primary', variant: 'outlined', onClick: () => {
                    props.onChangeOrganizationIds(props.organizationIds.filter((organizationId) => activeOrganizationId !== organizationId));
                    dataTreeProps.onChangeActiveItemId(undefined);
                } }, t('panels.organizations.removeOrgs')))),
        props.organizationIds.length > 0 && (React.createElement(DataTree, Object.assign({}, dataTreeProps, { getItemLabel: (item) => item.name, itemSelectionEnabled: false, border: true, itemContentComponent: (organization) => {
                const itemId = organization.item.id.split('_')[0];
                return (React.createElement(Box, { key: itemId, sx: {
                        py: { xs: 2, md: 0 },
                        pr: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        flexGrow: 1,
                        alignItems: { xs: undefined, sm: 'center' },
                    } }, props.renderOrganization(organization.item)));
            } }))),
        props.organizationIds.length === 0 && !props.hideValidationMessage && (React.createElement(FormLabel, { required: !!props.organizationSelectionRequired, sx: {
                '& .MuiFormLabel-asterisk': {
                    color: (theme) => theme.palette.error.main,
                },
                '& .MuiFormLabel-root': {
                    color: (theme) => theme.palette.text.primary,
                },
            } }, t('panels.organizations.noOrgsSelected')))));
};
